import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FIELDS } from "@/components/AddSuretyLead";
import { toast } from "sonner";
import { Pencil, Mail, MailOpen, Phone, MessageSquare, Trash2, Loader2 } from "lucide-react";


const METHODS = [
  ["email", "Email", Mail, "Send the AI email to this contractor?"],
  ["postcard", "Postcard", MailOpen, "Mail a postcard to this contractor? Postcards are billed per card."],
  ["call", "Call", Phone, "Have Ava call this contractor now?"],
  ["text", "Text", MessageSquare, "Send Ava's text to this contractor?"],
];

const EditDialog = ({ l, onClose, onDone }) => {
  const [f, setF] = useState(Object.fromEntries([...FIELDS.map(([k]) => [k, l[k] || ""]), ["notes", l.notes || ""]]));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/surety/leads/${l.id}`, f); toast.success("Lead updated"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="surety-edit-dialog">
        <DialogHeader><DialogTitle>Edit {l.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <div key={k}><Label className="text-xs">{label}</Label>
              <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`surety-edit-${k}`} /></div>
          ))}
          <div className="sm:col-span-2"><Label className="text-xs">Notes</Label>
            <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="surety-edit-notes" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy || f.name.trim().length < 2} data-testid="surety-edit-save">Save changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SuretyLeadActions = ({ l, onChanged }) => {
  const [busy, setBusy] = useState("");
  const [edit, setEdit] = useState(false);
  const contact = async ([m, label, , ask]) => {
    if (!window.confirm(ask)) return;
    setBusy(m);
    try { await api.post(`/surety/leads/${l.id}/contact/${m}`); toast.success(`${label} sent to ${l.name}`); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${l.name}? This can't be undone.`)) return;
    try { await api.delete(`/surety/leads/${l.id}`); toast.success("Lead deleted"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid={`surety-lead-actions-${l.id}`}>
      <Button size="sm" variant="outline" className="h-7" onClick={() => setEdit(true)} data-testid={`surety-lead-edit-${l.id}`}><Pencil className="mr-1 h-3 w-3" />Edit</Button>
      {METHODS.map((x) => {
        const [m, label, Icon] = x;
        return (
          <Button key={m} size="sm" variant="outline" className="h-7" disabled={!!busy} onClick={() => contact(x)} data-testid={`surety-lead-${m}-${l.id}`}>
            {busy === m ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Icon className="mr-1 h-3 w-3" />}{label}
          </Button>
        );
      })}
      <Button size="sm" variant="ghost" className="h-7 text-red-700 hover:bg-red-50" onClick={remove} data-testid={`surety-lead-delete-${l.id}`}><Trash2 className="mr-1 h-3 w-3" />Delete</Button>
      {edit && <EditDialog l={l} onClose={() => setEdit(false)} onDone={() => { setEdit(false); onChanged(); }} />}
    </div>
  );
};
